/**
 * Utility functions for determining chip colors and styles based on video data
 */

// Base chip styles that can be extended
export const baseChipStyle = {
  padding: '4px 8px',
  borderRadius: '16px',
  fontSize: '0.75rem',
  fontWeight: '500',
};

// Video chip color logic
export const getVideoChipStyle = (video) => {
  const baseStyle = { ...baseChipStyle };

  // Green - finalized cloud locations
  if (video.streamUrl) {
    return {
      ...baseStyle,
      background: '#e8f5e8',
      color: '#2e7d32',
      border: '1px solid #2e7d32',
    };
  }

  // Green with "YouTube" text - YouTube stream
  if (video.youtubeStreamUrl) {
    return {
      ...baseStyle,
      background: '#e8f5e8',
      color: '#2e7d32',
      border: '1px solid #2e7d32',
    };
  }

  // Yellow - trackside video
  if (video.tracksideVideoUrl) {
    if (video.vidUploadInitiated) {
      // Green outline if upload initiated
      return {
        ...baseStyle,
        background: '#fff3e0',
        color: '#f57c00',
        border: '1px solid #2e7d32',
      };
    } else {
      // Yellow outline
      return {
        ...baseStyle,
        background: '#fff3e0',
        color: '#f57c00',
        border: '1px solid #f57c00',
      };
    }
  }

  // Red - temp local video
  if (video.tempLocalVideoLink) {
    if (video.vidUploadInitiated) {
      // Green outline if upload initiated
      return {
        ...baseStyle,
        background: '#ffebee',
        color: '#d32f2f',
        border: '1px solid #2e7d32',
      };
    } else {
      // Red outline
      return {
        ...baseStyle,
        background: '#ffebee',
        color: '#d32f2f',
        border: '1px solid #d32f2f',
      };
    }
  }

  // No video
  return {
    ...baseStyle,
    background: '#f5f5f5',
    color: '#666',
  };
};

// Video chip text logic
export const getVideoChipText = (video) => {
  if (video.streamUrl) return 'Video';
  if (video.youtubeStreamUrl) return 'YouTube';
  if (video.tracksideVideoUrl) return 'Video';
  if (video.tempLocalVideoLink) return 'Video';
  return 'No Video';
};

// Lap data chip - always green if exists
export const getLapDataChipStyle = (hasLapData) => {
  if (hasLapData) {
    return {
      ...baseChipStyle,
      background: '#e8f5e8',
      color: '#2e7d32',
      border: '1px solid #2e7d32',
    };
  }
  return null;
};

// Telemetry chip style - matches video color logic
export const getTelemetryChipStyle = (telemetry) => {
  const baseStyle = { ...baseChipStyle };

  // Green - finalized cloud location
  if (telemetry.telemetryS3Key) {
    return {
      ...baseStyle,
      background: '#e8f5e8',
      color: '#2e7d32',
      border: '1px solid #2e7d32',
    };
  }

  // Red - temp local telemetry
  if (telemetry.tempLocalTelemetryLink) {
    if (
      telemetry.tempLocalTelemetryUploadStatus === 'completed' ||
      telemetry.tempLocalTelemetryUploadStatus === 'uploading'
    ) {
      // Green outline if upload completed or in progress
      return {
        ...baseStyle,
        background: '#ffebee',
        color: '#d32f2f',
        border: '1px solid #2e7d32',
      };
    } else {
      // Red outline
      return {
        ...baseStyle,
        background: '#ffebee',
        color: '#d32f2f',
        border: '1px solid #d32f2f',
      };
    }
  }

  // Yellow - trackside telemetry
  if (telemetry.tracksideTelemetryUrl) {
    if (
      telemetry.tracksideTelemetryUploadStatus === 'completed' ||
      telemetry.tracksideTelemetryUploadStatus === 'uploading'
    ) {
      // Green outline if upload completed or in progress
      return {
        ...baseStyle,
        background: '#fff3e0',
        color: '#f57c00',
        border: '1px solid #2e7d32',
      };
    } else {
      // Yellow outline
      return {
        ...baseStyle,
        background: '#fff3e0',
        color: '#f57c00',
        border: '1px solid #f57c00',
      };
    }
  }

  // No telemetry
  return null;
};

// Telemetry chip text logic
export const getTelemetryChipText = (telemetry) => {
  if (telemetry.telemetryS3Key) return 'Telemetry';
  if (telemetry.tempLocalTelemetryLink) return 'Telemetry';
  if (telemetry.tracksideTelemetryUrl) return 'Telemetry';
  return null;
};

// Helper function to get all chip data for a video
export const getVideoChips = (video) => {
  const chips = [];

  // Video chip
  const videoChip = {
    text: getVideoChipText(video),
    style: getVideoChipStyle(video),
  };
  chips.push(videoChip);

  // Telemetry chip
  const telemetryStyle = getTelemetryChipStyle(video);
  if (telemetryStyle) {
    chips.push({
      text: getTelemetryChipText(video),
      style: telemetryStyle,
    });
  }

  // Lap data chip
  const hasLapData = !!video.laptimeData;
  if (hasLapData) {
    chips.push({
      text: 'Laptimes',
      style: getLapDataChipStyle(hasLapData),
    });
  }

  return chips;
};
